<script lang="ts" setup>
	import StringMethods from '~/utils/character'
	import type { IconLibProps } from '../tools/icon.vue'
	import type { AtomBtnConfigClass } from './btn.vue'
	import type { uiTagConfig } from './pill.vue'
	import type { ClassNameValue } from 'tailwind-merge'

	export type H1Config = {
		tag?: Partial<uiTagConfig>
		wrapperClass?: Partial<{
			alignItems: string
			maxWidth: string
			text: string
			overflow: string
			base: string
			default: string
		}>
		hClass?: Partial<{
			wrapper: Partial<{
				base: string
				font: string
				text: string
				size: string
				leading: string
				alignItems: string
				position: string
			}>
			highlight: Partial<{
				color: string
				position: string
				width: string
				display: string
				padding: string
				transition: string
				base: string
			}>
		}>
		paragraphClass?: Partial<{
			text: string
			color: string
			maxWidth: string
			leading: string
			font: string
			base: string
		}>
		btn?: Partial<{
			base: string
			transition: string
			space: string
			padding: string
			primary: Partial<AtomBtnConfigClass>
			secondary: Partial<{ base: string; text: string; iconProps: Partial<IconLibProps> }>
			iconWrapper: Partial<{
				base: string
				text: string
				maxWidth: string
			}>
		}>
	}
	const config: H1Config = {
		wrapperClass: {
			alignItems: 'md:justify-center md:text-center md:items-center text-left',
			maxWidth: 'max-w-[1032px] md:mx-auto',
			text: 'text-white',
			overflow: 'overflow-hidden',
			default: 'flex text-ellipsis flex-col'
		},
		hClass: {
			wrapper: {
				font: 'maitree-bold',
				size: 'text-[2em] xs:text-[2.6em] text-[inherit] lg:text-[4.25em] lg:leading-[80px] xs:leading-[50px] leading-[40px]',
				text: 'text-balance',
				alignItems: 'items-center justify-center',
				position: 'relative',
				base: 'md:inline-flex flex-wrap'
			},
			highlight: {
				color: 'text-[#FFB908] ',
				position: '',
				transition: 'custom-transition-2'
			}
		},
		paragraphClass: {
			text: 'md:text-xl xs:text-base text-sm',
			color: 'text-[#FAFBFD]',
			maxWidth: 'max-w-[847px] mx-auto',
			leading: 'sm:leading-[32px] xs:leading-[22px]',
			font: ' !font-normal'
		},

		btn: {
			base: 'md:flex w-full flex-col md:justify-center md:items-center',
			transition: 'transition ease-linear',
			space: 'xs:space-y-4',
			padding: 'md:pt-0  pt-10',
			primary: {
				btn: {
					onHoverPseudo: 'hover:before:bg-base-primary',
					base: 'bg-white text-sm xs:text-base text-base-700  hover:text-white'
				}
			},
			secondary: {
				base: 'inline-flex items-center gap-x-2',
				text: 'text-white '
			},
			iconWrapper: {
				base: 'inline-flex sm:items-center sm:justify-center flex-col',
				text: 'md:text-center ',
				maxWidth: ''
			}
		}
	}
	const {
		wrapperClass,
		hClass: { highlight, wrapper },
		paragraphClass,
		btn
	} = config as Required<H1Config>

	const props = withDefaults(
		defineProps<{
			title?: string
			animateTitle?: string[]
			paragraph?: string
			primaryBtnText?: string
			secondaryBtnText?: string
			ui?: Partial<H1Config>
			tag?: string
			tagIcon?: IconLibProps
			hClass?: ClassNameValue
			action?: {
				type: 'onboard' | ''
				product?: string | number
			}
			btnProps?: {
				secondary?: Object
				primary?: Object
			}
		}>(),
		{
			btnProps: () => ({
				secondary: {
					id: 'intercom_custom_btn'
				}
			})
		}
	)

	const emits = defineEmits<{
		(event: 'onPrimary'): void
		(event: 'onSecondary'): void
	}>()
	const count = ref(0)
	const interVal = ref()

	const play = () => {
		if (props.animateTitle?.length) {
			if (count.value < props.animateTitle.length - 1) {
				count.value++
			} else {
				count.value = 0
			}
		}
	}

	onMounted(() => {
		if (props.animateTitle?.length) {
			interVal.value = setInterval(play, 3000)
		}
	})

	onUnmounted(() => {
		clearInterval(interVal.value)
	})
	const onPrimaryTrigger = () => {
		if (props.action?.type === 'onboard') {
			if (props.action.product) {
				const { navigateToProduct } = useProductsStore()
				navigateToProduct(props.action.product)
			}
		} else {
			emits('onPrimary')
		}
	}
	const onSecondaryTrigger = () => {
		const { toLower } = StringMethods()
		if (toLower(props.secondaryBtnText).includes('speak')) {
			useMixpanel()?.track('speak_to_us', {
				product: props?.action?.product ?? 'home page'
			})
		}
		emits('onSecondary')
	}
</script>

<template>
	<div>
		<ui-pill
			v-if="!!tag"
			:label="tag"
			:icon-prop="tagIcon"
			:ui="{
				base: ui?.tag?.base ?? 'bg-main text-white',
				default: 'mb-4 md:hidden',
				...(ui?.tag || {})
			}"
		/>
		<div
			:class="
				cn(
					ui?.wrapperClass?.alignItems ?? wrapperClass.alignItems,
					ui?.wrapperClass?.maxWidth ?? wrapperClass.maxWidth,
					ui?.wrapperClass?.overflow ?? wrapperClass.overflow,
					ui?.wrapperClass?.text ?? wrapperClass.text,
					ui?.wrapperClass?.base ?? wrapperClass?.base,
					ui?.wrapperClass?.default ?? wrapperClass?.default
				)
			"
		>
			<slot name="top" />
			<slot>
				<ui-title
					:class="[ui?.hClass?.wrapper?.alignItems ?? (wrapper!).alignItems,
					ui?.hClass?.wrapper?.font ?? (wrapper!).font,
					ui?.hClass?.wrapper?.position ?? (wrapper!).position,
					ui?.hClass?.wrapper?.text ?? (wrapper!).text,
					ui?.hClass?.wrapper?.size ?? (wrapper!).size,
					ui?.hClass?.wrapper?.leading ?? (wrapper!).leading,
					ui?.hClass?.wrapper?.base ?? (wrapper!)?.base ?? '', hClass]"
				>
					<slot name="title" :highlight-color="[ui?.hClass?.highlight?.color ?? (highlight!).color]">
						<span v-html="title"></span>
					</slot>
					<span
						v-if="animateTitle?.length"
						class="overflow-hidden"
						:class="[
							cn(ui?.hClass?.highlight?.color ?? (highlight!).color,
							ui?.hClass?.highlight?.display ?? (highlight!).display,
							ui?.hClass?.highlight?.padding ?? (highlight!).padding,
							ui?.hClass?.highlight?.position ?? (highlight!).position,
							ui?.hClass?.highlight?.width ?? (highlight!).width,
							ui?.hClass?.highlight?.transition ?? (highlight!).transition)
						]"
					>
						<template v-for="(item, index) in animateTitle" :key="'animated-text' + index">
							<span
								class="absolute lg:inline-block text-center hidden mx-auto lg:left-0 lg:right-0 w-fit"
								:class="[
									{ active: index === count, 'in-active opacity-0': index !== count },
									ui?.hClass?.highlight?.color ?? (highlight!).color,
									ui?.hClass?.highlight?.transition ?? (highlight!).transition
								]"
							>
								{{ item }}
							</span>
							<span
								:class="{
									'block custom-transition-2': index === count,
									'invisible absolute': index !== count
								}"
								class="lg:opacity-0 overflow-clip"
							>
								{{ item }}
							</span>
						</template>
					</span>
				</ui-title>

				<p
					v-show="paragraph"
					:class="
						cn([
							ui?.paragraphClass?.color ?? paragraphClass.color,
							ui?.paragraphClass?.font ?? paragraphClass.font,
							ui?.paragraphClass?.leading ?? paragraphClass.leading,
							ui?.paragraphClass?.maxWidth ?? paragraphClass.maxWidth,
							ui?.paragraphClass?.text ?? paragraphClass.text,
							ui?.paragraphClass?.base ?? 'mt-4'
						])
					"
					v-html="paragraph"
				></p>
			</slot>

			<div
				v-show="primaryBtnText || secondaryBtnText"
				:class="
					cn([
						ui?.btn?.base ?? btn?.base,
						ui?.btn?.padding ?? btn?.padding,
						ui?.btn?.space ?? btn?.space,
						ui?.btn?.transition ?? btn?.transition
					])
				"
			>
				<div
					:class="
						cn([
							ui?.btn?.iconWrapper?.base ?? btn?.iconWrapper?.base,
							ui?.btn?.iconWrapper?.maxWidth ?? btn?.iconWrapper?.maxWidth,
							ui?.btn?.iconWrapper?.text ?? btn?.iconWrapper?.text
						])
					"
				>
					<ui-btn
						v-if="primaryBtnText"
						:class="['text-base py-4']"
						name="onboard"
						:ui="{
							...(ui?.btn?.primary ?? {}),
							btn: {
								...(ui?.btn?.primary?.btn ?? {}),
								base: ui?.btn?.primary?.btn?.base ?? btn?.primary?.btn?.base,
								onHoverPseudo: ui?.btn?.primary?.btn?.onHoverPseudo ?? btn?.primary?.btn?.onHoverPseudo
							}
						}"
						v-bind="btnProps.primary"
						@click="onPrimaryTrigger"
					>
						<span v-html="primaryBtnText" class="relative z-10" />
					</ui-btn>

					<button
						v-if="secondaryBtnText"
						type="button"
						class="group text-center default-btn justify-center mt-3"
						:class="
							cn([
								ui?.btn?.secondary?.base ?? btn.secondary?.base,
								ui?.btn?.secondary?.text ?? btn.secondary?.text
							])
						"
						v-bind="btnProps?.secondary"
						@click="onSecondaryTrigger"
					>
						<span class="group-hover:border-b border-white ease-linear group-hover:pb-1 transition-all">
							{{ secondaryBtnText }}
						</span>
						<span class="custom-transition-3 group-hover:translate-x-3 sm:text-base">
							<tools-icon dir="arrows" icon="arrow-short-r" v-bind="ui?.btn?.secondary?.iconProps" />
						</span>
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
	.in-active:not(.active) {
		@apply translate-y-28;
	}
</style>
